import React, { useState } from 'react';
import { toast } from 'react-hot-toast';

export default function KeywordPlannerTest() {
  const [seed, setSeed] = useState('kasko\ntrafik sigortası');
  const [geo, setGeo] = useState('TR');
  const [lang, setLang] = useState('tr');
  const [dateRange, setDateRange] = useState('LAST_12_MONTHS');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [maxResults, setMaxResults] = useState(100);
  const [oneKeyword, setOneKeyword] = useState('wunder');
  const [monthly, setMonthly] = useState(null);
  const [loadingMonthly, setLoadingMonthly] = useState(false);

  const run = async () => {
    try {
      setLoading(true);
      setResult(null);
      const seedKeywords = seed
        .split('\n')
        .map(s => s.trim())
        .filter(Boolean);
      if (seedKeywords.length === 0) {
        toast.error('En az bir anahtar kelime girin');
        return;
      }
      const res = await fetch('https://api.glowytics.com/api/labs/keyword-planner/run', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ seedKeywords, geoTarget: geo, language: lang, dateRange, maxResults: Number(maxResults) || 100 })
      });
      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        let msg = 'Run failed';
        if (ct.includes('application/json')) {
          const js = await res.json().catch(() => ({}));
          msg = js.error || js.details || msg;
        }
        throw new Error(msg);
      }
      const data = await res.json();
      setResult(data);
      toast.success('Run completed');
    } catch (e) {
      console.error('KeywordPlanner run error:', e);
      toast.error(e.message || 'Run failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-semibold mb-4">Labs · Keyword Planner (Historical Search Terms)</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Seed Keywords (one per line)</label>
          <textarea
            className="w-full h-32 border rounded-lg p-3"
            value={seed}
            onChange={(e) => setSeed(e.target.value)}
          />
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Geo Target</label>
            <input className="w-full border rounded-lg p-2" value={geo} onChange={(e) => setGeo(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
            <input className="w-full border rounded-lg p-2" value={lang} onChange={(e) => setLang(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <select className="w-full border rounded-lg p-2" value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
              <option value="LAST_12_MONTHS">Last 12 months</option>
              <option value="LAST_24_MONTHS">Last 24 months</option>
              <option value="CUSTOM">Custom (script-dependent)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max Results</label>
            <input type="number" className="w-full border rounded-lg p-2" value={maxResults} onChange={(e) => setMaxResults(e.target.value)} />
          </div>
        </div>
      </div>
      <button
        onClick={run}
        disabled={loading}
        className={`px-4 py-2 rounded-lg font-medium ${loading ? 'bg-gray-300 text-gray-600' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
      >
        {loading ? 'Running…' : 'Run'}
      </button>

      <div className="mt-8 p-4 border rounded-lg">
        <h2 className="text-lg font-semibold mb-3">Single Keyword · Monthly Volume</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Keyword</label>
            <input className="w-full border rounded-lg p-2" value={oneKeyword} onChange={(e) => setOneKeyword(e.target.value)} />
          </div>
          <div>
            <button
              onClick={async () => {
                try {
                  setLoadingMonthly(true);
                  setMonthly(null);
                  const brandId = localStorage.getItem('currentBrandId');
                  const res = await fetch('https://api.glowytics.com/api/labs/keyword-planner/monthly-volume', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) },
                    body: JSON.stringify({ keyword: oneKeyword, months: 0 })
                  });
                  const ct = res.headers.get('content-type') || '';
                  if (!res.ok) {
                    let msg = 'Request failed';
                    if (ct.includes('application/json')) { const js = await res.json().catch(() => ({})); msg = js.error || msg; }
                    throw new Error(msg);
                  }
                  const data = await res.json();
                  setMonthly(data);
                } catch (e) {
                  console.error('Monthly volume error:', e);
                  toast.error(e.message || 'Request failed');
                } finally {
                  setLoadingMonthly(false);
                }
              }}
              disabled={loadingMonthly}
              className={`px-4 py-2 rounded-lg font-medium ${loadingMonthly ? 'bg-gray-300 text-gray-600' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
            >
              {loadingMonthly ? 'Loading…' : 'Get Monthly Volume'}
            </button>
          </div>
        </div>

        {monthly && (
          <div className="mt-4">
            {Array.isArray(monthly.monthly) && monthly.monthly.length > 0 ? (
              <div className="overflow-x-auto border rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-2">Year</th>
                      <th className="text-left p-2">Month</th>
                      <th className="text-left p-2">Monthly Searches</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthly.monthly.map((m, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="p-2">{m.year}</td>
                        <td className="p-2">{String(m.month).padStart(2, '0')}</td>
                        <td className="p-2">{m.monthly_searches}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <pre className="bg-gray-50 p-3 rounded-lg text-xs overflow-x-auto">{JSON.stringify(monthly, null, 2)}</pre>
            )}
          </div>
        )}
      </div>

      {result && (
        <div className="mt-6">
          <h2 className="text-lg font-semibold mb-2">Result</h2>
          {Array.isArray(result.terms) && result.terms.length > 0 ? (
            <div className="overflow-x-auto border rounded-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-2">Term</th>
                    <th className="text-left p-2">Avg Monthly Searches</th>
                    <th className="text-left p-2">Competition</th>
                  </tr>
                </thead>
                <tbody>
                  {result.terms.map((t, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="p-2">{t.term}</td>
                      <td className="p-2">{t.avgMonthlySearches}</td>
                      <td className="p-2">{t.competition}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <pre className="bg-gray-50 p-3 rounded-lg text-xs overflow-x-auto">{JSON.stringify(result, null, 2)}</pre>
          )}
        </div>
      )}
    </div>
  );
}


